import { cn } from '@/lib/cn';

export interface ChipOption<T extends string | number> {
  value: T;
  label: string;
  /** Segunda línea pequeña (p. ej. «Safe» bajo «15%»). */
  sublabel?: string;
  /** Nombre accesible si el rótulo solo no basta. */
  ariaLabel?: string;
}

export interface ChipGroupProps<T extends string | number> {
  /** Nombre accesible del grupo. */
  label: string;
  options: ChipOption<T>[];
  /** Un valor que no está en `options` deja todos sin pulsar. */
  value: T | null;
  onChange: (value: T) => void;
  /** `grid` = columnas iguales. */
  layout?: 'wrap' | 'grid';
  /** `md` 44 px; `lg` 52 px (con sublabel). */
  size?: 'md' | 'lg';
  className?: string;
}

/** Selección única en fichas. Seleccionada = relleno de acento con texto oscuro (sirve en claro y en oscuro). */
export function ChipGroup<T extends string | number>({
  label,
  options,
  value,
  onChange,
  layout = 'wrap',
  size = 'md',
  className,
}: ChipGroupProps<T>) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(layout === 'grid' ? 'grid gap-1.5' : 'flex flex-wrap gap-1.5', className)}
      style={layout === 'grid' ? { gridTemplateColumns: `repeat(${Math.max(1, options.length)}, minmax(0, 1fr))` } : undefined}
    >
      {options.map((o) => {
        const on = value !== null && o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={on}
            aria-label={o.ariaLabel}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex min-w-11 flex-col items-center justify-center border text-[13px] font-medium transition-colors motion-reduce:transition-none',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-fg-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-primary)]',
              size === 'lg' ? 'min-h-[52px] gap-0.5 rounded-xl px-2' : 'min-h-11 rounded-full px-3.5',
              on
                ? 'border-[var(--color-xk-accent)] bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)]'
                : 'border-[var(--color-border-strong)] bg-[var(--color-bg-surface)] text-[var(--color-fg-primary)] hover:bg-[var(--color-bg-elevated)]',
            )}
          >
            <span className={cn(o.sublabel && 'font-bold')}>{o.label}</span>
            {o.sublabel ? <span className="text-[11px] opacity-80">{o.sublabel}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
